import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, AlertCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CompareBatches = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const batchIds = searchParams.get('ids')?.split(',') || [];

  useEffect(() => {
    if (batchIds.length < 2) {
      navigate('/batches');
      return;
    }
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const requests = batchIds.map(id => axios.get(`${BACKEND_URL}/api/batches/${id}`));
      const responses = await Promise.all(requests);
      setBatches(responses.map(r => r.data));
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const getComparison = (metric, batches) => {
    const values = batches.map(b => ({ batch: b, value: b[metric] }));
    const sorted = [...values].sort((a, b) => a.value - b.value);
    return {
      best: metric === 'total_co2_kg' ? sorted[0] : sorted[sorted.length - 1],
      worst: metric === 'total_co2_kg' ? sorted[sorted.length - 1] : sorted[0],
    };
  };

  const getRecommendation = (batches) => {
    const avgCO2 = batches.reduce((sum, b) => sum + b.total_co2_kg, 0) / batches.length;
    const highCO2 = batches.filter(b => b.total_co2_kg > avgCO2);
    
    if (highCO2.length > 0) {
      return `Consider optimizing transport routes for ${highCO2.map(b => b.batch_number).join(', ')} to reduce CO₂ emissions by ~15%.`;
    }
    return 'All batches show optimal performance. Continue current practices.';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NO', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading comparison...</p>
        </div>
      </div>
    );
  }

  const co2Comparison = getComparison('total_co2_kg', batches);
  const integrityComparison = getComparison('integrity_score', batches);

  return (
    <div className="space-y-8" data-testid="compare-batches-page">
      <Button variant="ghost" onClick={() => navigate('/batches')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Batches
      </Button>

      <div>
        <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
          Batch Comparison
        </h1>
        <p className="text-lg text-slate-600">
          Compare {batches.length} batches across key metrics
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-4 px-6 text-sm font-semibold text-slate-900">Metric</th>
                {batches.map((batch, idx) => (
                  <th key={idx} className="text-left py-4 px-6">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{batch.batch_number}</p>
                      <p className="text-xs text-slate-600">{batch.product_name}</p>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="py-4 px-6 text-sm font-medium text-slate-900">Product</td>
                {batches.map((batch, idx) => (
                  <td key={idx} className="py-4 px-6 text-sm text-slate-700">{batch.product_name}</td>
                ))}
              </tr>

              <tr>
                <td className="py-4 px-6 text-sm font-medium text-slate-900">Origin</td>
                {batches.map((batch, idx) => (
                  <td key={idx} className="py-4 px-6 text-sm text-slate-700">{batch.origin.name}</td>
                ))}
              </tr>

              <tr>
                <td className="py-4 px-6 text-sm font-medium text-slate-900">Catch Date</td>
                {batches.map((batch, idx) => (
                  <td key={idx} className="py-4 px-6 text-sm text-slate-700">{formatDate(batch.catch_date)}</td>
                ))}
              </tr>

              <tr>
                <td className="py-4 px-6 text-sm font-medium text-slate-900">Weight (kg)</td>
                {batches.map((batch, idx) => (
                  <td key={idx} className="py-4 px-6 text-sm text-slate-700">{batch.weight_kg.toLocaleString()}</td>
                ))}
              </tr>

              <tr className="bg-teal-50/30">
                <td className="py-4 px-6">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-slate-900">CO₂ Footprint (kg)</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="ml-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs cursor-help">
                            Est
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">Estimated (PoC)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </td>
                {batches.map((batch, idx) => {
                  const isBest = co2Comparison.best.batch.id === batch.id;
                  const isWorst = co2Comparison.worst.batch.id === batch.id;
                  return (
                    <td key={idx} className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${
                          isBest ? 'text-emerald-700' : isWorst ? 'text-rose-700' : 'text-slate-700'
                        }`}>
                          {batch.total_co2_kg}
                        </span>
                        {isBest && <TrendingDown className="w-4 h-4 text-emerald-600" />}
                        {isWorst && <TrendingUp className="w-4 h-4 text-rose-600" />}
                      </div>
                    </td>
                  );
                })}
              </tr>

              <tr className="bg-blue-50/30">
                <td className="py-4 px-6">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-slate-900">Integrity Score</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-xs">
                            Integrity Score reflects data completeness, event verification, and traceability continuity within the PureSustain platform. Not a certification or regulatory guarantee.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </td>
                {batches.map((batch, idx) => {
                  const isBest = integrityComparison.best.batch.id === batch.id;
                  const isWorst = integrityComparison.worst.batch.id === batch.id;
                  return (
                    <td key={idx} className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${
                          isBest ? 'text-emerald-700' : isWorst ? 'text-rose-700' : 'text-slate-700'
                        }`}>
                          {batch.integrity_score}/100
                        </span>
                        {isBest && <TrendingUp className="w-4 h-4 text-emerald-600" />}
                        {isWorst && <TrendingDown className="w-4 h-4 text-rose-600" />}
                      </div>
                    </td>
                  );
                })}
              </tr>

              <tr>
                <td className="py-4 px-6 text-sm font-medium text-slate-900">Current Status</td>
                {batches.map((batch, idx) => (
                  <td key={idx} className="py-4 px-6 text-sm text-slate-700">{batch.current_status}</td>
                ))}
              </tr>

              <tr>
                <td className="py-4 px-6 text-sm font-medium text-slate-900">Compliance</td>
                {batches.map((batch, idx) => {
                  const compliant = batch.compliance.filter(c => c.status === 'compliant').length;
                  const total = batch.compliance.length;
                  const rate = (compliant / total * 100).toFixed(0);
                  return (
                    <td key={idx} className="py-4 px-6">
                      <span className={`text-sm font-semibold ${
                        rate === '100' ? 'text-emerald-700' : rate >= '80' ? 'text-blue-700' : 'text-amber-700'
                      }`}>
                        {rate}% ({compliant}/{total})
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-slate-100 rounded-lg">
            <AlertCircle className="w-6 h-6 text-slate-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Decision-Support Insights</h3>
            <p className="text-slate-700 mb-2">{getRecommendation(batches)}</p>
            <p className="text-xs text-slate-600 italic">
              Insights are indicative and intended to support operational planning. Actual outcomes may vary.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">Best CO₂ Performance</h3>
          <p className="text-2xl font-bold text-slate-800 mb-1">{co2Comparison.best.batch.batch_number}</p>
          <p className="text-sm text-slate-600">{co2Comparison.best.value} kg CO₂</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">Highest Integrity</h3>
          <p className="text-2xl font-bold text-slate-800 mb-1">{integrityComparison.best.batch.batch_number}</p>
          <p className="text-sm text-slate-600">{integrityComparison.best.value}/100 score</p>
        </div>
      </div>
    </div>
  );
};

export default CompareBatches;
